import React from 'react';
import { Box, Typography } from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import MicIcon from '@mui/icons-material/Mic';
import LandingLayout from './components/LandingLayout';
import { LogoImg } from 'src/assets/images';
import handoutImg from 'src/assets/images/for-rgr/handout-photo.jpg';
import lessonCatalogImg from 'src/assets/images/for-rgr/lesson-catalog.png';
import atAGlanceImg from 'src/assets/images/for-rgr/at-a-glance.png';
import readAloudPromptImg from 'src/assets/images/for-rgr/read-aloud-prompt.png';
import readAloudBuddyImg from 'src/assets/images/for-rgr/read-aloud-buddy.png';
import heartWordsImg from 'src/assets/images/for-rgr/heart-words.png';
import gradeLevelImg from 'src/assets/images/for-rgr/grade-level.png';
import multilingualImg from 'src/assets/images/for-rgr/multilingual.png';
import trainingWheelsImg from 'src/assets/images/for-rgr/training-wheels.png';

const PRESSTO_NAVY = '#163567';
const PRESSTO_PURPLE = '#5C4FB8';
const PRESSTO_CREAM = '#faf7f0';
const PRESSTO_CREAM_BORDER = '#d4c9a8';
const TEXT_DARK = '#1e1e1e';
const TEXT_MUTED = '#555';

const SERIF = '"Georgia", "Times New Roman", serif';
const SANS = 'Inter, "Helvetica Neue", Arial, sans-serif';

// ─── Reusable slot components ──────────────────────────────────────────────────
function ScreenshotSlot({ label, ratio = 62.5 }: { label: string; ratio?: number }) {
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        paddingTop: `${ratio}%`,
        backgroundColor: PRESSTO_CREAM,
        border: `2px dashed ${PRESSTO_CREAM_BORDER}`,
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: PRESSTO_NAVY,
          gap: 1,
          opacity: 0.6,
        }}
      >
        <ImageOutlinedIcon sx={{ fontSize: 48 }} />
        <Typography
          sx={{
            fontFamily: SANS,
            fontSize: '0.8rem',
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          Screenshot · {label}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Hero ──────────────────────────────────────────────────────────────────────
function Hero() {
  return (
    <Box
      sx={{
        backgroundColor: PRESSTO_NAVY,
        color: '#FFFFFF',
        py: { xs: 4, md: 5 },
        px: { xs: 3, md: 6 },
        textAlign: 'center',
        position: 'relative',
      }}
    >
      <Box
        component="img"
        src={LogoImg}
        alt="Pressto"
        sx={{
          position: 'absolute',
          top: { xs: 18, md: 22 },
          left: { xs: 18, md: 28 },
          height: { xs: 24, md: 30 },
          width: 'auto',
          filter: 'brightness(0) invert(1)',
        }}
      />
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: SERIF,
            fontWeight: 700,
            fontSize: { xs: '1.75rem', md: '2.5rem' },
            lineHeight: 1.1,
            letterSpacing: '-0.01em',
          }}
        >
          RGR + Pressto ={' '}
          <Box component="span" sx={{ fontStyle: 'italic', color: '#F4A6B8' }}>
            Really Great Writing
          </Box>
        </Typography>
        <Typography
          sx={{
            fontFamily: SANS,
            fontSize: { xs: '0.95rem', md: '1.1rem' },
            color: 'rgba(255,255,255,0.82)',
            mt: 2.5,
            lineHeight: 1.55,
            maxWidth: 760,
            mx: 'auto',
          }}
        >
          {`Pressto's superpower is that it's delightful and fun to use. Teachers love it. Implementation is easy, which helps make outcome-based pricing successful.`}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Tour Loom (centerpiece) ──────────────────────────────────────────────────
function TourLoom() {
  return (
    <Box
      id="tour"
      sx={{
        py: { xs: 8, md: 12 },
        px: { xs: 3, md: 6 },
        backgroundColor: PRESSTO_CREAM,
        scrollMarginTop: '80px',
      }}
    >
      <Box sx={{ maxWidth: 1080, mx: 'auto' }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
          <Typography
            component="h2"
            sx={{
              fontFamily: SERIF,
              fontWeight: 700,
              fontSize: { xs: '1.5rem', md: '2rem' },
              color: TEXT_DARK,
              letterSpacing: '-0.01em',
            }}
          >
            Pressto in five minutes.
          </Typography>
        </Box>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            paddingTop: '56.25%',
            backgroundColor: TEXT_DARK,
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 12px 32px rgba(22,53,103,0.18)',
          }}
        >
          <Box
            component="iframe"
            src="https://www.loom.com/embed/4af0ad18cb1c4b3589820cc23c0645e5"
            title="Pressto tour — full walkthrough"
            allow="fullscreen"
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              border: 'none',
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

// ─── Hero feature row ──────────────────────────────────────────────────────────
function HeroFeature({
  number,
  eyebrow,
  title,
  body,
  screenshotLabel,
  screenshotSrc,
  reverse = false,
  anchor,
}: {
  number: string;
  eyebrow: string;
  title: React.ReactNode;
  body: React.ReactNode;
  screenshotLabel: string;
  screenshotSrc?: string | string[];
  reverse?: boolean;
  anchor?: string;
}) {
  const sources = screenshotSrc
    ? (Array.isArray(screenshotSrc) ? screenshotSrc : [screenshotSrc])
    : [];
  return (
    <Box
      id={anchor}
      sx={{
        py: { xs: 8, md: 12 },
        px: { xs: 3, md: 6 },
        backgroundColor: '#FFFFFF',
        scrollMarginTop: '80px',
      }}
    >
      <Box
        sx={{
          maxWidth: 1200,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' },
          gap: { xs: 4, md: 8 },
          alignItems: 'center',
        }}
      >
        <Box sx={{ order: { xs: 1, md: reverse ? 2 : 1 } }}>
          <Typography
            sx={{
              fontFamily: SANS,
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: PRESSTO_PURPLE,
              mb: 2,
            }}
          >
            {number} · {eyebrow}
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontFamily: SERIF,
              fontWeight: 700,
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              lineHeight: 1.1,
              color: TEXT_DARK,
              mb: 3,
              letterSpacing: '-0.01em',
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontFamily: SANS,
              fontSize: '1.05rem',
              lineHeight: 1.7,
              color: TEXT_MUTED,
            }}
          >
            {body}
          </Typography>
        </Box>
        <Box sx={{ order: { xs: 2, md: reverse ? 1 : 2 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {sources.length > 0 ? (
            sources.map((src, i) => (
              <Box
                key={src}
                component="img"
                src={src}
                alt={`${screenshotLabel}${sources.length > 1 ? ` (${i + 1})` : ''}`}
                sx={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '8px',
                  boxShadow: '0 8px 24px rgba(22,53,103,0.12)',
                  display: 'block',
                }}
              />
            ))
          ) : (
            <ScreenshotSlot label={screenshotLabel} ratio={62.5} />
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ─── Secondary card ────────────────────────────────────────────────────────────
function SecondaryCard({
  title,
  body,
  accent = PRESSTO_PURPLE,
  imageSrc,
}: {
  title: string;
  body: string;
  accent?: string;
  imageSrc?: string;
}) {
  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        borderRadius: '10px',
        border: '1px solid #E5E5E5',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 120ms ease-in-out',
        '&:hover': {
          borderColor: accent,
          transform: 'translateY(-2px)',
          boxShadow: '0 6px 16px rgba(0,0,0,0.06)',
        },
      }}
    >
      {imageSrc && (
        <Box
          sx={{
            width: '100%',
            aspectRatio: '16 / 10',
            backgroundColor: PRESSTO_CREAM,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
        >
          <Box
            component="img"
            src={imageSrc}
            alt={title}
            sx={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </Box>
      )}
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ height: 4, width: 32, backgroundColor: accent, borderRadius: '2px', mb: 1 }} />
      <Typography
        sx={{
          fontFamily: SERIF,
          fontWeight: 700,
          fontSize: '1.15rem',
          color: TEXT_DARK,
          letterSpacing: '-0.01em',
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontFamily: SANS,
          fontSize: '0.95rem',
          lineHeight: 1.6,
          color: TEXT_MUTED,
        }}
      >
        {body}
      </Typography>
      </Box>
    </Box>
  );
}

function SecondaryGrid() {
  const cards: Array<{ title: string; body: string; imageSrc?: string }> = [
    {
      title: 'Heart Words',
      body: 'A toggleable panel for the unit\'s high-frequency sight words. The chip lights up the first time a student uses each word in their writing.',
      imageSrc: heartWordsImg,
    },
    {
      title: 'Printable Handout',
      body: 'Any assignment becomes an 8.5×11 print-ready PDF in one click — graphic organizer, sentence starters, and all.',
      imageSrc: handoutImg,
    },
    {
      title: 'Grade-Level Writing Buddy',
      body: 'AI feedback tuned to the student\'s grade — reading level, sentence length, and tone. No 6th-grade words for a 2nd grader.',
      imageSrc: gradeLevelImg,
    },
    {
      title: 'Multi-lingual support for ELL students',
      body: 'Students can read prompts and Writing Buddy feedback in Spanish, Vietnamese, Russian, Ukrainian, Mandarin, and more — or auto-detect from the browser. Equity built in from the start.',
      imageSrc: multilingualImg,
    },
    {
      title: 'Training wheels for learners',
      body: 'Three levels of writing supports — graphic organizer + sentence starters, organizer only, or independent. Teachers dial scaffolding up or down per assignment.',
      imageSrc: trainingWheelsImg,
    },
  ];
  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        px: { xs: 3, md: 6 },
        backgroundColor: PRESSTO_CREAM,
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
          <Typography
            component="h2"
            sx={{
              fontFamily: SERIF,
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.5rem' },
              color: TEXT_DARK,
              letterSpacing: '-0.01em',
            }}
          >
            Smaller features
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 3,
          }}
        >
          {cards.map((c) => (
            <SecondaryCard key={c.title} title={c.title} body={c.body} imageSrc={c.imageSrc} />
          ))}
        </Box>
      </Box>
    </Box>
  );
}


// ─── Page ──────────────────────────────────────────────────────────────────────
export default function ForRGRPage() {
  return (
    <LandingLayout hideFooter hideNav>
      <Hero />

      <TourLoom />

      <HeroFeature
        number="01"
        eyebrow="Curriculum-Aligned Library"
        title="Gallery of writing activities and handouts aligned to RGR"
        body="A curated library of writing activities aligned to Countdown, Blast Foundations, HD Word, and HD Word Plus. Each card opens a fresh, fully-scaffolded assignment in a teacher's gallery — ready to assign in one click, fully editable by the teacher who adopts it."
        screenshotLabel="Lesson Catalog"
        screenshotSrc={lessonCatalogImg}
      />

      <HeroFeature
        number="02"
        eyebrow="Teacher's At-a-Glance Dashboard"
        title="Student progress, support, and AI detection at a glance"
        body={
          <>
            {`As students write, the teacher sees the whole classroom in a single view: who's started, who's stuck on which block, how many words each student has written, which Heart Words and Important Words are showing up in their writing, how long they've been actively typing — and, quietly, who pasted text instead of typing it.`}
            <br /><br />
            {`Paste and pacing are signals for a conversation, not verdicts. A student pasting a quoted line from a book is doing legitimate research; a student pasting 200 words at once may have copied from a source. The teacher decides.`}
          </>
        }
        screenshotLabel="At-a-Glance Dashboard"
        screenshotSrc={atAGlanceImg}
        reverse
      />

      <HeroFeature
        number="03"
        eyebrow="Multi-Modal Accessibility"
        title={
          <Box component="span" sx={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1.5, rowGap: 1 }}>
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              Read Aloud
              <VolumeUpIcon sx={{ fontSize: '1.6rem', color: PRESSTO_PURPLE }} />
            </Box>
            <Box component="span" sx={{ color: TEXT_MUTED, fontWeight: 400 }}>and</Box>
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              Dictation
              <MicIcon sx={{ fontSize: '1.6rem', color: PRESSTO_PURPLE }} />
            </Box>
          </Box>
        }
        body="Every prompt, every Writing Buddy message, and every block can be read aloud. Students who'd rather speak than type dictate any block directly into the editor with OpenAI Whisper. Built from the start for ELL learners, special-ed students, and any kid whose ideas move faster than their fingers."
        screenshotLabel="Read Aloud"
        screenshotSrc={[readAloudPromptImg, readAloudBuddyImg]}
      />

      <SecondaryGrid />

    </LandingLayout>
  );
}
